// ─────────────────────────────────────────────────────────────────────────────
// useActivity — GET /api/rexy/activity + /api/rexy/activity/detail.
// Shared core (owner ruling 2026-07-31, "one core, every window").
//
// Drill-in is a GATE, not a render concern: which rows may expand, and when a
// detail is fetched, is the same on every surface. Only ONE row is expanded at a
// time, and the detail cache is shared so a re-expand never re-fetches.
//
// Expanded state survives each 30s refresh (expandedRef is independent of the
// index), and when a refresh flips the expanded row pending → read/absent the
// detail fills in live without a manual reload.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { authHeaders, POLL_MS } from './authHeaders';
import { ACTIVITY, activityDetail, GROWTH_ACTIVITY, growthActivityDetail } from './endpoints';
import {
  ActivityDetail,
  ActivityIndexResponse,
  ActivityRow,
  FIXTURE_DETAILS,
  FIXTURE_INDEX,
} from '../../components/dashboard/activityFixtures';

export type { ActivityDetail, ActivityIndexResponse, ActivityRow };

// ── Part-1 seam ──────────────────────────────────────────────────────────────
// USE_FIXTURES = true was the Part-1 (fixtures-only) seam so the owner could
// eyeball every signed state before the backend existed. PART 2 FLIPPED THIS
// (integration pass, 2026-07-21): the feed is LIVE against the real endpoints;
// fixtures remain only as the type/contract reference. Carried across the core
// extraction unchanged — behaviour is identical with it false.
const USE_FIXTURES: boolean = false;

/**
 * WHICH ACTIVITY LANE A MOUNT READS (owner ruling 2026-09-01).
 *
 * The response SHAPE is identical, so the render, the banding, the four-state
 * chip and the drill-in gate are all shared. What differs is which pair of
 * endpoints answers — and behind them, three things that could not be shared:
 * the scope, the payload join, and the clock. See `core/alpha_activity.py`.
 *
 * A LANE IS A VALUE, NOT A BRANCH. Nothing below asks "is this Rexy" — the two
 * lanes are data, so a third one costs a constant and no code.
 */
export type ActivityLane = {
  /** Stable identity, for a test id and a cache key. */
  key: string;
  index: string;
  detail: (ref: string) => string;
};

/** HOMEPAGE — Rexy's own. THE DEFAULT, so her call site is unchanged. */
export const REXY_LANE: ActivityLane = {
  key: 'rexy', index: ACTIVITY, detail: activityDetail,
};

/** GROWTH ▸ CEO — Alpha's. */
export const GROWTH_LANE: ActivityLane = {
  key: 'growth', index: GROWTH_ACTIVITY, detail: growthActivityDetail,
};

export type DetailSlot =
  | { state: 'loading' }
  | { state: 'error'; error: string }
  | { state: 'ok'; detail: ActivityDetail };

export const ACTIVITY_KIND_LABEL: Record<ActivityRow['source_kind'], string> = {
  agent_dashboard: 'dashboard',
  domain_board: 'board',
  chat: 'chat',
};

// Drill-in detail atom fields, rendered in signed order, only when present.
export const DETAIL_FIELDS: Array<keyof ActivityDetail> = [
  'when', 'who', 'where', 'what', 'why', 'how', 'outcome', 'status',
];

export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function hhmm(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '--:--';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// Next-pulse marker label from next_pulses[0] — pulses run on the hour.
export function nextPulseHour(nextPulses: string[]): string {
  if (!nextPulses || nextPulses.length === 0) return '--:00';
  const d = new Date(nextPulses[0]);
  if (isNaN(d.getTime())) return '--:00';
  return `${pad2(d.getHours())}:00`;
}

export function pulseOutcome(
  pulse: NonNullable<ActivityIndexResponse['groups'][number]['pulse']>,
): string {
  if (pulse.status === 'complete') {
    const at = pulse.executed_at ? hhmm(pulse.executed_at) : '--:--';
    return `pulse ✓ ${at} · ${pulse.events_written} events`;
  }
  if (pulse.status === 'missed') return 'pulse ✗ missed';
  return `pulse ${pulse.status}`;
}

export type ActivityFeed = {
  index: ActivityIndexResponse | null;
  err: string | null;
  updatedAt: string | null;
  expandedRef: string | null;
  details: Record<string, DetailSlot>;
  toggleRow: (row: ActivityRow) => void;
  loadDetail: (ref: string) => void;
};

export function useActivity(lane: ActivityLane = REXY_LANE): ActivityFeed {
  const [index, setIndex] = useState<ActivityIndexResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expandedRef, setExpandedRef] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, DetailSlot>>({});
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  useEffect(() => {
    if (USE_FIXTURES) {
      setIndex(FIXTURE_INDEX);
      return;
    }
    const fetchIndex = () => {
      fetch(lane.index, { headers: authHeaders() })
        .then((r) => r.json())
        .then((j) => {
          if (j && j.success) {
            setIndex(j as ActivityIndexResponse);
            setErr(null);
            const d = new Date();
            setUpdatedAt(`${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`);
          } else {
            setErr((j && j.error) || 'unavailable');
          }
        })
        .catch((e) => setErr(String(e)));
    };
    fetchIndex();
    const poll = setInterval(fetchIndex, POLL_MS);
    return () => clearInterval(poll);
    // THE LANE IS A DEPENDENCY. Omitting it would leave a remounted panel
    // polling the endpoint of whichever lane mounted first — one feed wearing
    // another's label, on the surface whose whole job is to be checkable.
  }, [lane]);

  // The cache guard reads through a ref, never a stale closure — and NEVER from
  // inside a setState updater: React StrictMode double-invokes updaters, so a
  // fetch in there would fire twice per drill-in.
  const detailsRef = useRef<Record<string, DetailSlot>>({});
  detailsRef.current = details;

  // Drill-in — separate authed call, 64-hex-scoped (signed). Fixture-gated in Part 1.
  const loadDetail = useCallback((ref: string) => {
    if (detailsRef.current[ref]) return; // cached (or in flight)
    if (USE_FIXTURES) {
      const d = FIXTURE_DETAILS[ref];
      setDetails((prev) => ({
        ...prev,
        [ref]: d ? { state: 'ok', detail: d } : { state: 'error', error: 'detail unavailable' },
      }));
      return;
    }
    setDetails((prev) => ({ ...prev, [ref]: { state: 'loading' } }));
    fetch(lane.detail(ref), { headers: authHeaders() })
      .then((r) => r.json())
      .then((j) =>
        setDetails((prev) => ({
          ...prev,
          [ref]: j && j.success && j.detail
            ? { state: 'ok', detail: j.detail as ActivityDetail }
            : { state: 'error', error: (j && j.error) || 'unavailable' },
        }))
      )
      .catch((e) =>
        setDetails((prev) => ({ ...prev, [ref]: { state: 'error', error: String(e) } }))
      );
  }, [lane]);

  // When a refresh flips the expanded row to read/absent and no detail is cached
  // yet, fetch its detail so the expansion fills in live (pending → ✓ read with
  // no manual reload). loadDetail's cache guard makes this idempotent.
  useEffect(() => {
    if (USE_FIXTURES || !index || !expandedRef) return;
    const row = index.groups
      .flatMap((g) => g.rows)
      .find((r) => r.journal_ref === expandedRef);
    if (row && (row.status === 'read' || row.status === 'absent') && !details[expandedRef]) {
      loadDetail(expandedRef);
    }
  }, [index, expandedRef, details, loadDetail]);

  // Only one row expanded at a time.
  const toggleRow = useCallback((row: ActivityRow) => {
    if (expandedRef === row.journal_ref) {
      setExpandedRef(null);
      return;
    }
    setExpandedRef(row.journal_ref);
    if (row.status === 'read' || row.status === 'absent') loadDetail(row.journal_ref);
  }, [expandedRef, loadDetail]);

  return { index, err, updatedAt, expandedRef, details, toggleRow, loadDetail };
}
